import { jsx } from "react/jsx-runtime";
import { useThemeProps, styled } from "@mui/material/styles";
import { motion } from "motion/react";
import { resolveThemeColor, createResponsiveStyles } from "../../theme/util.js";
import React__default from "react";
const ButtonBase = styled(motion.button)(() => ({
  fontFamily: "inherit",
  lineHeight: "inherit"
}));
const convertHeightToResponsiveStyles = (height) => {
  if (height == null) {
    return;
  }
  const heightValue = typeof height === "number" || typeof height === "string" ? {
    xs: height,
    sm: height,
    md: height,
    lg: height,
    xl: height
  } : height;
  return Object.entries(heightValue).reduce((acc, [key, value]) => {
    acc[key] = {
      height: value
    };
    return acc;
  }, {});
};
const SolidButton = styled(ButtonBase, {
  name: "BCSolidButton",
  slot: "Root"
})(({ theme, ownerState }) => {
  var _a, _b, _c;
  const {
    color = "primary",
    buttonName,
    disableMaxWidth,
    increaseSpecificity,
    height,
    fullWidth,
    disableResponsiveStyles
  } = ownerState;
  const themeColor = resolveThemeColor(theme, color);
  const cssStyleOverrides = buttonName && ((_a = theme.componentCSSOverrides) == null ? void 0 : _a[buttonName]);
  const heightStyles = convertHeightToResponsiveStyles(height);
  const defaultStyles = {
    backgroundColor: themeColor.main,
    color: themeColor.contrastText,
    ...fullWidth && { width: "100%" },
    ...disableResponsiveStyles ? {} : createResponsiveStyles(theme, heightStyles, cssStyleOverrides)
  };
  const styles = {
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    font: theme.vars.font.button,
    fontWeight: (_b = theme.typography.button) == null ? void 0 : _b.fontWeight,
    ...defaultStyles,
    ":hover": {
      // this is redundant but neccessary to prevent third party client style overrides
      ...defaultStyles
    },
    transition: "transform 0.3s",
    "&:active": {
      "&:not([disabled])": {
        transform: "scale(0.95)"
      }
    },
    "not(:focus-visible)": {
      outline: "none"
    },
    padding: "8.5px 17px",
    // padding top and bottom of 7px plus 1.5px for border
    ":disabled": {
      cursor: "not-allowed",
      opacity: 1
    },
    [theme.breakpoints.down("sm")]: {
      font: theme.vars.font.smButton,
      fontWeight: (_c = theme.typography.smButton) == null ? void 0 : _c.fontWeight,
      ...!disableMaxWidth && { maxWidth: 333 }
    },
    ".left_icon": {
      marginRight: 6
    },
    ".button_img": {
      marginRight: 6,
      maxWidth: 18,
      maxHeight: 18
    }
  };
  if (increaseSpecificity) {
    return {
      "&&": {
        ...styles
      }
    };
  }
  return styles;
});
const OutlineButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => !["disableMaxWidth", "sx", "increaseSpecificity"].includes(prop),
  name: "BCOutlineButton",
  slot: "Root"
})(({ theme, ownerState }) => {
  var _a, _b, _c;
  const {
    color = "primary",
    buttonName,
    disableMaxWidth,
    increaseSpecificity,
    height,
    fullWidth,
    disableResponsiveStyles
  } = ownerState;
  const themeColor = resolveThemeColor(theme, color);
  const cssStyleOverrides = buttonName && ((_a = theme.componentCSSOverrides) == null ? void 0 : _a[buttonName]);
  const heightStyles = convertHeightToResponsiveStyles(height);
  const defaultStyles = {
    color: themeColor.main,
    borderColor: themeColor.main,
    backgroundColor: "transparent",
    ...fullWidth && { width: "100%" },
    ...disableResponsiveStyles ? {} : createResponsiveStyles(theme, heightStyles, cssStyleOverrides)
  };
  const styles = {
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    position: "relative",
    font: theme.vars.font.button,
    fontWeight: (_b = theme.typography.button) == null ? void 0 : _b.fontWeight,
    ...defaultStyles,
    ":hover": {
      ...defaultStyles
    },
    transition: "transform 0.3s",
    "&:active": {
      "&:not([disabled])": {
        transform: "scale(0.95)"
      }
    },
    "not(:focus-visible)": {
      outline: "none"
    },
    padding: "7px 17px",
    ":disabled": {
      opacity: 1,
      color: theme.vars.palette.action.disabled,
      cursor: "not-allowed",
      borderColor: theme.vars.palette.action.disabled
    },
    [theme.breakpoints.down("sm")]: {
      font: theme.vars.font.smButton,
      fontWeight: (_c = theme.typography.smButton) == null ? void 0 : _c.fontWeight,
      ...!disableMaxWidth && { maxWidth: 333 }
    },
    ".button_img": {
      marginRight: 6,
      maxWidth: 18,
      maxHeight: 18
    }
  };
  if (increaseSpecificity) {
    return {
      "&&": {
        ...styles
      }
    };
  }
  return styles;
});
const TextButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => !["sx", "increaseSpecificity"].includes(prop),
  name: "BCTextButton",
  slot: "Root"
})(({ theme, ownerState }) => {
  var _a, _b, _c;
  const { color = "primary", buttonName, increaseSpecificity, fullWidth, disableResponsiveStyles } = ownerState;
  const resolvedColor = color === "default" ? theme.vars.palette.text.primary : (_a = resolveThemeColor(theme, color)) == null ? void 0 : _a.main;
  const cssStyleOverrides = buttonName && ((_b = theme.componentCSSOverrides) == null ? void 0 : _b[buttonName]);
  const defaultStyles = {
    color: resolvedColor,
    backgroundColor: "transparent",
    ...fullWidth && { width: "100%" },
    ...disableResponsiveStyles ? {} : createResponsiveStyles(theme, cssStyleOverrides)
  };
  const styles = {
    font: theme.vars.font.button,
    fontWeight: (_c = theme.typography.button) == null ? void 0 : _c.fontWeight,
    ...defaultStyles,
    textTransform: "unset",
    // resets any potentially added text transformation; if required, add them in the sx prop where needed
    width: "auto",
    height: "auto",
    minWidth: "fit-content",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "unset",
    "not(:focus-visible)": {
      outline: "none"
    },
    border: "none",
    padding: 0,
    margin: 0,
    fontSize: "inherit",
    ":disabled": {
      ...defaultStyles,
      cursor: "not-allowed",
      opacity: 1,
      color: theme.vars.palette.action.disabled
    },
    ".left_icon": {
      marginRight: 6
    },
    ".button_img": {
      marginRight: 6,
      maxWidth: 18,
      maxHeight: 18
    }
  };
  if (increaseSpecificity) {
    return {
      "&&": {
        ...styles
      }
    };
  }
  return styles;
});
const IconButton = styled(TextButton, {
  shouldForwardProp: (prop) => !["sx", "increaseSpecificity"].includes(prop),
  name: "BCIconButton",
  slot: "Root",
  skipVariantsResolver: true
})(() => {
  return {};
});
const Button = React__default.forwardRef(({ variant = "solid", ...inProps }, ref) => {
  const ButtonComponentName = {
    solid: "BCSolidButton",
    outline: "BCOutlineButton",
    text: "BCTextButton",
    icon: "BCIconButton"
  }[variant];
  const props = useThemeProps({
    props: inProps,
    name: ButtonComponentName
  });
  const { disableMaxWidth, color, buttonName, increaseSpecificity, height, ...rest } = props;
  const ownerState = {
    ...props,
    variant,
    disableMaxWidth,
    color,
    buttonName,
    increaseSpecificity,
    height
  };
  switch (variant) {
    case "solid": {
      if (props.disabled) {
        return /* @__PURE__ */ jsx(
          OutlineButton,
          {
            ref,
            type: "button",
            ownerState,
            "data-theme-color": color,
            "data-theme-variant": "outline-button",
            "data-theme-component-name": buttonName,
            ...rest
          }
        );
      }
      return /* @__PURE__ */ jsx(
        SolidButton,
        {
          ref,
          type: "submit",
          ownerState,
          "data-theme-color": color,
          "data-theme-variant": "solid-button",
          "data-theme-component-name": buttonName,
          ...rest
        }
      );
    }
    case "outline":
      return /* @__PURE__ */ jsx(
        OutlineButton,
        {
          ref,
          type: "button",
          ownerState,
          "data-theme-color": color,
          "data-theme-variant": "outline-button",
          "data-theme-component-name": buttonName,
          ...rest
        }
      );
    case "text":
      return /* @__PURE__ */ jsx(
        TextButton,
        {
          ref,
          type: "button",
          ownerState,
          "data-theme-color": color,
          "data-theme-variant": "text-button",
          "data-theme-component-name": buttonName,
          ...rest
        }
      );
    case "icon":
      return /* @__PURE__ */ jsx(
        IconButton,
        {
          ref,
          type: "button",
          ownerState,
          "data-theme-color": color,
          "data-theme-variant": "icon-button",
          "data-theme-component-name": buttonName,
          disableResponsiveStyles: true,
          ...rest
        }
      );
    default:
      throw new Error(`Invalid variant: ${variant}. Expected 'solid', 'outline', or 'text'.`);
  }
});
Button.displayName = "Button";
export {
  Button
};
//# sourceMappingURL=Button.js.map
